import React from 'react';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { useAppActions, useAppState } from '../../../overmind';
import { ActivationStatus, FetchType } from '../../../enums';
import { PersonActivationStatusIcon, createDepartmentLink, createTeamLink } from './people.cms.helpers';
import { IPeopleTableCMSLayoutProps } from './people.cms.models';
import { PersonListItem } from '../../../models/view/employee-search-response';
import { Anchor, Button, Chip, formatDate, KebabMenu, MenuItem, Pager, Table, TableColumn, TableRow, useMatchScreenWidth } from '@keplerco/core';
import { PagePath } from '../../../navigation/navigation.enums';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import styles from './people.module.css';
import { ActivationStatusDescription } from '../../../enums/activation-status';

export function PeopleTableCMSLayout(props: IPeopleTableCMSLayoutProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const keplerNavigate = useKeplerNavigate();
  const isMobile = useMatchScreenWidth('mobile');

  // tables
  async function onSortHandler(column: any) {
    actions.startLoader({ path: props.path, type: FetchType.PageFetching });

    const isCurrentlyAscending = props.request.sortAscending;
    const currentSortField = props.request.sortField;

    const newSortDirection = currentSortField === column ? !isCurrentlyAscending : true;

    props.setRequest({ ...props.request, sortField: column, sortAscending: newSortDirection });
  }

  async function onPageChangeHandler(page: number) {
    if (page === props.request.page) return;
    props.setSelectedRows([]);

    actions.startLoader({ path: props.path, type: FetchType.PageFetching });
    props.setRequest({ ...props.request, page });
  }

  async function onClickPersonHandler(learnerSlug: string | undefined) {
    if (!learnerSlug) return;

    actions.startLoader({ path: props.path, type: FetchType.Custom });
    props.setPersonToManage({ show: true, data: learnerSlug });
    actions.stopLoader(props.path);
  }

  // kebab menus
  async function onClickArchiveHandler(person: PersonListItem) {
    if (!person.learnerSlug) return;
    props.setPersonToArchive(person);
  }

  async function onClickUnarchiveHandler(person: PersonListItem) {
    if (!person.learnerSlug || person.activationStatus !== ActivationStatus.Archived) return;

    props.setLoadingPeople([...props.loadingPeople, person.learnerSlug]);
    await actions.unarchivePerson(person.learnerSlug);
    props.setLoadingPeople([...props.loadingPeople.filter(pid => person.learnerSlug !== pid)]);
  }

  async function onClickDeleteHandler(person: PersonListItem) {
    if (!person.learnerSlug) return;
    props.setPersonToDelete(person);
  }

  function generateKebabMenuItems(person: PersonListItem): MenuItem[] {
    const isArchived = person.activationStatus === ActivationStatus.Archived;
    const isNotInvited = person.activationStatus === ActivationStatus.NotInvited;
    const isInactive = person.activationStatus === ActivationStatus.Inactive;

    return [
      {
        label: 'Manage',
        onClick: () => onClickPersonHandler(person.learnerSlug),
        disabled: isArchived,
      },
      {
        label: isArchived ? 'Unarchive' : 'Archive',
        onClick: () => (isArchived ? onClickUnarchiveHandler(person) : onClickArchiveHandler(person)),
      },
      {
        label: 'Delete',
        onClick: () => onClickDeleteHandler(person),
        disabled: !isArchived,
      },
      {
        label: 'Send activation invitation',
        onClick: () => props.onClickSendActivationInvitation({ learnerSlugs: [person.learnerSlug!], shouldClearRows: false }),
        disabled: !isNotInvited || !person.learnerSlug,
      },
      {
        label: 'Send activation reminder',
        onClick: () => props.onClickSendActivationReminder({ learnerSlugs: [person.learnerSlug!], shouldClearRows: false }),
        disabled: !isInactive || !person.learnerSlug,
      },
    ];
  }

  function getArrayColumnText(names: string[] | undefined): string {
    let result = '';
    if (names === undefined) {
      result = '';
    }
    else if (names.length < 2) {
      result = names[0] || '';
    }
    else if (names.length === 2) {
      result = `${names[0]} (+ ${names.length - 1} other)`;
    } else {
      result = `${names[0]} (+ ${names.length - 1} others)`;
    }
    return result;
  }


  return (
    <Table
      selectableRows
      currentSortBy={String(props.request.sortField)}
      onSort={onSortHandler}
      currentSortDirection={props.request.sortAscending ? 'Ascending' : 'Descending'}
      selectedRows={props.selectedRows}
      onRowSelection={props.setSelectedRows}
      emptyState={
        <EmptyState
          title="No one has been added yet"
          buttonElements={[
            <Button key="Bulk Upload" type="button" onClick={() => keplerNavigate(`${PagePath.userManagementBase}${PagePath.userManagementPeopleBulkUpload.replace(':companySlug', companyVariables.slug!)}`)}>
              Bulk Upload
            </Button>,
          ]}
        />
      }
      onConfigClicked={() => props.setManageColumns({ open: true })}
      footerContent={
        <PagerConnector onPageChange={onPageChangeHandler} defaultPageNumber={props.request.page ?? 1} pageCount={props.data?.totalPages ?? 1}>
          {connector => {
            return <Pager {...connector} />;
          }}
        </PagerConnector>
      }
    >
      {props.data?.employees.map(person => {
        const isLoading = props.loadingPeople.includes(person.learnerSlug!);

        return (
          <TableRow
            configCell={() => {
              return (
                <span key={person.learnerSlug} style={{ visibility: isLoading ? 'hidden' : undefined, pointerEvents: isLoading ? 'none' : undefined }}>
                  <KebabMenu items={generateKebabMenuItems(person)} />
                </span>
              );
            }}
            id={person.learnerSlug!}
            key={person.learnerSlug}
            isLoading={isLoading}
          >
            <TableColumn id="Name" label="Name" sortable>
              <div className={styles.peopleNameCell}>
                <PersonActivationStatusIcon status={person.activationStatus} />
                {`${person.firstName} ${person.lastName}`}
              </div>
            </TableColumn>

            <TableColumn sortable={false} hidden={!props.columnConfiguration.find(column => column.key === 'Role')?.selected} id="Role" label="Role">
              {getArrayColumnText(person.roles?.map(role => role.name))}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Department')?.selected} id="Department" label="Department">
              {!!person.department?.slug && (
                <Anchor
                  hovertype="opacity"
                  textTransform="none"
                  onClick={event => {
                    event.stopPropagation();
                    keplerNavigate(createDepartmentLink(props.companySlug!, person.department.slug!));
                  }}
                >
                  {person.department.name}
                </Anchor>
              )}

              {!person.department.slug && <React.Fragment>{person.department.name}</React.Fragment>}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Team')?.selected} id="Team" label="Team">
              {!!person.team.slug && (
                <Anchor
                  hovertype="opacity"
                  textTransform="none"
                  onClick={event => {
                    event.stopPropagation();
                    keplerNavigate(createTeamLink(props.companySlug!, person.team.slug!));
                  }}
                >
                  {person.team.name}
                </Anchor>
              )}

              {!person.team.slug && <React.Fragment>{person.team.name}</React.Fragment>}
            </TableColumn>

            <TableColumn sortable={false} hidden={!props.columnConfiguration.find(column => column.key === 'Manager')?.selected} id="Manager" label="Line manager">
              {getArrayColumnText(person.managers)}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Email')?.selected} id="Email" label="Email">
              {isMobile && person.email.length > 14 ? `${person.email.slice(0, 14)}...` : person.email}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Country')?.selected} id="Country" label="Country">
              {person.country.name}
            </TableColumn>

            <TableColumn hidden={!props.columnConfiguration.find(column => column.key === 'Status')?.selected} id="Status" label="Status">
              {person.activationStatus === ActivationStatus.NotInvited && <Chip themeColour="r">{ActivationStatusDescription.NotInvited}</Chip>}
              {person.activationStatus === ActivationStatus.Inactive && <Chip themeColour="a">{ActivationStatusDescription.Inactive}</Chip>}
              {person.activationStatus === ActivationStatus.Active && <Chip themeColour="g">{ActivationStatusDescription.Active}</Chip>}
              {person.activationStatus === ActivationStatus.Archived && <Chip>{ActivationStatusDescription.Archived}</Chip>}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'DateCreated')?.selected} id="DateCreated" label="Date Created">
              {!!person.dateCreated && formatDate(new Date(person.dateCreated))}
            </TableColumn>
          </TableRow>
        );
      })}
    </Table>
  );
}
